import { Select as S, Switch as Sw } from 'radix-ui';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../utils';

export function Select<T extends string>({
  value,
  onValueChange,
  options,
  disabled,
  className,
}: {
  value: T;
  onValueChange: (v: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
  className?: string;
}) {
  return (
    <S.Root value={value} onValueChange={(v) => onValueChange(v as T)} disabled={disabled}>
      <S.Trigger
        className={cn(
          'inline-flex h-8 min-w-0 items-center justify-between gap-2 whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
          className,
        )}
      >
        {/* Long values stay on one line: cut with an ellipsis, full text as tooltip. */}
        <span
          className="min-w-0 flex-1 truncate text-left"
          title={options.find((o) => o.value === value)?.label}
        >
          <S.Value />
        </span>
        <S.Icon className="shrink-0">
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={4}
          className="z-50 min-w-[8rem] max-w-[min(22rem,calc(100vw-2rem))] rounded-md border border-border bg-popover p-1 shadow-lg"
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted"
              >
                <S.ItemText>{o.label}</S.ItemText>
                <S.ItemIndicator>
                  <Check className="size-3.5 text-accent" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  label,
}: {
  checked: boolean;
  onCheckedChange: (c: boolean) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <Sw.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className="relative h-5 w-9 shrink-0 rounded-full bg-border transition-colors data-[state=checked]:bg-accent disabled:opacity-50"
    >
      <Sw.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[18px]" />
    </Sw.Root>
  );
}
